import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Users, Truck, Clock, MapPin, DollarSign, Calendar, CheckCircle, ArrowLeft, Package, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import mockData from '../mock';
import { useToast } from '../hooks/use-toast';

const FinalQuote = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { 
    selectedDate, 
    hourlyRate, 
    propertyType, 
    propertySize, 
    floorType, 
    crewRecommendation,
    originAddress,
    destinationAddress,
    routeData,
    additionalServices = {},
    bulkyItemsCount = 1,
    additionalDetails = '',
    additionalCosts = 0
  } = location.state || {};

  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    specialInstructions: additionalDetails
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate estimated time and cost with new pricing logic
  const estimatedHours = mockData.estimateMovingTime(
    propertySize, 
    crewRecommendation?.men || 2, 
    floorType,
    parseFloat(routeData?.distance || '5'),
    crewRecommendation?.trucks || 1
  );

  // Extract distance in miles from routeData.distance (format: "10.5 mi")
  const distanceInMiles = parseFloat((routeData?.distance || '5 mi').replace(/[^\d.]/g, '') || '5');

  // Use new cost calculation method with distance
  const costBreakdown = mockData.calculateTotalCost(
    hourlyRate,
    estimatedHours,
    crewRecommendation?.trucks || 1,
    additionalCosts,
    distanceInMiles
  );

  const handleInputChange = (field, value) => {
    setCustomerInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitQuote = async () => {
    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    // Prepare booking data
    const bookingData = {
      customer_name: customerInfo.name,
      customer_email: customerInfo.email,
      customer_phone: customerInfo.phone,
      selected_date: selectedDate,
      hourly_rate: hourlyRate,
      property_type: propertyType,
      property_size: propertySize,
      floor_type: floorType,
      origin_address: originAddress,
      destination_address: destinationAddress,
      distance: routeData?.distance || 'N/A',
      duration: routeData?.duration || 'N/A',
      crew_men: crewRecommendation?.men || 2,
      crew_trucks: crewRecommendation?.trucks || 1,
      additional_services: additionalServices,
      special_instructions: customerInfo.specialInstructions || '',
      estimated_hours: Math.round(estimatedHours),
      total_cost: costBreakdown.grandTotal
    };

    try {
      // Get backend URL from environment variable
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      // Send to backend
      const response = await fetch(`${backendUrl}/api/bookings/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData)
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Booking Request Submitted Successfully!",
          description: `Booking ID: ${result.booking_id}. Redirecting to homepage...`,
        });
        
        // Redirect to homepage with success message
        setTimeout(() => {
          navigate('/', { 
            replace: true,
            state: { 
              bookingSuccess: true,
              bookingId: result.booking_id,
              customerEmail: customerInfo.email
            }
          });
        }, 2000);
      } else {
        throw new Error(result.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Booking submission error:', error);
      toast({
        title: "Submission Error",
        description: "Failed to submit booking. Please call (713) 487-9097 or email info@emisamovers.com directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    navigate('/additional-services', { 
      state: {
        selectedDate,
        hourlyRate,
        propertyType,
        propertySize,
        floorType,
        crewRecommendation,
        originAddress,
        destinationAddress,
        routeData
      },
      replace: true
    });
  };

  const getSelectedServicesList = () => {
    const serviceNames = {
      packingOnly: 'Packing Only (no materials included)',
      fullPacking: 'Full Packing Service (with supplies)',
      bulkyItems: `Bulky Items Handling (×${bulkyItemsCount})`,
      storage: 'Temporary Storage',
      disassembly: 'Furniture Disassembly/Assembly (Included)',
      cleaning: 'Post-Move Cleaning (Coming Soon)'
    };

    return Object.entries(additionalServices)
      .filter(([_, isSelected]) => isSelected)
      .map(([serviceId]) => serviceNames[serviceId])
      .filter(Boolean);
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#F8F7F4'}}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3 cursor-pointer group">
              <img 
                src="https://customer-assets.emergentagent.com/job_swiftmover/artifacts/7o75lsp7_Emisa%20Movers%20Logo.jpeg" 
                alt="Emisa Movers Logo"
                className="h-28 w-28 transition-all duration-300 transform group-hover:scale-110 group-hover:shadow-2xl group-active:scale-105 rounded-lg"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Emisa Movers</h1>
                <p className="text-xs text-amber-700 font-medium">GETS IT DONE ON TIME</p>
              </div>
            </a>
            <div className="flex items-center gap-3">
              <a 
                href="tel:7134879097"
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 hover:shadow-2xl hover:scale-110 text-white px-4 py-2 rounded-lg font-medium shadow-md transition-all duration-300 transform"
              >
                <Phone className="h-4 w-4" />
                (713) 487-9097
              </a>
              <a href="/" onClick={(e) => { e.preventDefault(); window.location.href = '/#book-now'; }} className="bg-teal-600 hover:bg-teal-700 hover:shadow-2xl hover:scale-110 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 transform">Book It NOW</a>
              <Button
                variant="outline"
                onClick={handleGoBack}
                className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white border-gray-600"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Services
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Title */}
        <div className="text-center mb-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Your Moving Quote
          </h1>
          <p className="text-sm sm:text-base text-gray-700">
            Review your details and get your personalized quote from Emisa Movers
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          {/* Left Column - Details and Route */}
          <div className="space-y-3">
            {/* Moving Details */}
            <Card>
              <CardHeader className="p-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Calendar className="h-4 w-4 text-teal-600" />
                  Moving Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 p-3 pt-0">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-600 text-xs">Date:</span>
                    <div className="font-medium text-teal-600 text-xs">{selectedDate}</div>
                  </div>
                  <div>
                    <span className="text-gray-600 text-xs">Property:</span>
                    <div className="font-medium text-teal-600 text-xs capitalize">{propertyType}</div>
                  </div>
                  <div>
                    <span className="text-gray-600 text-xs">Size:</span>
                    <div className="font-medium text-teal-600 text-xs">{propertySize} sqft</div>
                  </div>
                  <div>
                    <span className="text-gray-600 text-xs">Floor:</span>
                    <div className="font-medium text-teal-600 text-xs capitalize">{floorType?.replace('_', ' ') || 'N/A'}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Service Details */}
            <Card>
              <CardHeader className="p-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="h-4 w-4 text-teal-600" />
                  Service Included
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <Users className="h-6 w-6 text-teal-600" />
                    <div>
                      <div className="font-semibold text-teal-600 text-sm">{crewRecommendation?.men} Movers</div>
                      <div className="text-xs text-gray-600">Professional team</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Truck className="h-6 w-6 text-teal-600" />
                    <div>
                      <div className="font-semibold text-teal-600 text-sm">
                        {crewRecommendation?.trucks} Truck{crewRecommendation?.trucks > 1 ? 's' : ''}
                      </div>
                      <div className="text-xs text-gray-600">Fully equipped</div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-2 pt-2 border-t">
                  <h4 className="font-medium text-teal-600 text-xs mb-1">Included Services:</h4>
                  <ul className="text-xs text-gray-600 space-y-0.5">
                    <li className="flex items-center gap-1">
                      <CheckCircle className="h-3 w-3 text-teal-600" />
                      Professional packing materials
                    </li>
                    <li className="flex items-center gap-1">
                      <CheckCircle className="h-3 w-3 text-teal-600" />
                      Loading and unloading
                    </li>
                    <li className="flex items-center gap-1">
                      <CheckCircle className="h-3 w-3 text-teal-600" />
                      Transportation and fuel
                    </li>
                    <li className="flex items-center gap-1">
                      <CheckCircle className="h-3 w-3 text-teal-600" />
                      Basic moving insurance
                    </li>
                  </ul>
                </div>

                {/* Additional Services */}
                {getSelectedServicesList().length > 0 && (
                  <div className="mt-2 pt-2 border-t">
                    <h4 className="font-medium text-teal-600 text-xs mb-1 flex items-center gap-1">
                      <Package className="h-3 w-3" />
                      Additional Services:
                    </h4>
                    <ul className="text-xs text-gray-600 space-y-0.5">
                      {getSelectedServicesList().map((service, index) => (
                        <li key={index} className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3 text-red-600" />
                          {service}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Route Info */}
            <Card>
              <CardHeader className="p-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <MapPin className="h-4 w-4 text-teal-600" />
                  Route Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <div className="space-y-2 text-xs">
                  <div>
                    <span className="text-gray-600">From:</span>
                    <div className="font-medium text-teal-600 text-xs">{originAddress}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">To:</span>
                    <div className="font-medium text-teal-600 text-xs">{destinationAddress}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <div>
                      <span className="text-gray-600">Distance:</span>
                      <div className="font-medium text-teal-600 text-xs">{routeData?.distance}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Travel Time:</span>
                      <div className="font-medium text-teal-600 text-xs">{routeData?.duration}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Price Breakdown - Moved here after Route Info */}
            <Card className="border-gray-200 bg-white shadow-sm">
              <CardHeader className="p-3">
                <CardTitle className="flex items-center gap-2 text-teal-600 text-base">
                  <DollarSign className="h-4 w-4" />
                  Price Quote
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-600">Base Service</span>
                    <span className="text-gray-600">{estimatedHours}h × ${hourlyRate}/h</span>
                    <span className="font-medium text-teal-600">${costBreakdown.baseMovingCost}</span>
                  </div>
                  
                  {costBreakdown.truckMultiplier > 1 && (
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-gray-600">Two Truck Service</span>
                      <span className="text-gray-600">×{costBreakdown.truckMultiplier}</span>
                      <span className="font-medium text-teal-600">${costBreakdown.totalMovingCost}</span>
                    </div>
                  )}
                  
                  {/* Travel Fee - Always shown */}
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-600">Travel Fee</span>
                    <span className="text-xs text-gray-500">({distanceInMiles.toFixed(1)} mi)</span>
                    <span className="font-medium text-teal-600">${costBreakdown.travelFee}</span>
                  </div>

                  {costBreakdown.additionalCosts > 0 && (
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-gray-600">Additional Services</span>
                      <span></span>
                      <span className="font-medium text-red-600">${costBreakdown.additionalCosts}</span>
                    </div>
                  )}
                  
                  <div className="border-t pt-2 flex justify-between items-center text-base font-bold text-red-600">
                    <span>Estimated Total</span>
                    <span>${costBreakdown.grandTotal}</span>
                  </div>
                </div>
                
                <div className="mt-2 p-2 bg-teal-50 rounded-lg">
                  <div className="flex items-center gap-1 text-xs text-gray-600">
                    <Clock className="h-3 w-3" />
                    <span>Est. time: {estimatedHours}h</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-0.5">
                    * Final price may vary
                  </p>
                  <p className="text-xs text-teal-600 mt-0.5">
                    ✓ Travel: $65 up to 30mi, +$10/10mi after
                  </p>
                  {crewRecommendation?.trucks === 2 && (
                    <p className="text-xs text-teal-600 mt-0.5">
                      ✓ Two truck service included
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Contact Form and Booking */}
          <div className="space-y-3">

            {/* Contact Form */}
            <Card id="contact-form">
              <CardHeader className="p-3">
                <CardTitle className="text-teal-600 text-base">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 p-3 pt-0">
                <div>
                  <Label htmlFor="name" className="text-xs">Full Name *</Label>
                  <Input
                    id="name"
                    placeholder="John Smith"
                    value={customerInfo.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="mt-1 focus:ring-teal-600 focus:border-teal-600 text-sm h-8"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email" className="text-xs">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    value={customerInfo.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="mt-1 focus:ring-teal-600 focus:border-teal-600 text-sm h-8"
                  />
                </div>
                
                <div>
                  <Label htmlFor="phone" className="text-xs">Phone Number *</Label>
                  <Input
                    id="phone"
                    placeholder="(555) 123-4567"
                    value={customerInfo.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="mt-1 focus:ring-teal-600 focus:border-teal-600 text-sm h-8"
                  />
                </div>
                
                <div>
                  <Label htmlFor="instructions" className="text-xs">Special Instructions (Optional)</Label>
                  <Textarea
                    id="instructions"
                    placeholder="Any final notes..."
                    value={customerInfo.specialInstructions}
                    onChange={(e) => handleInputChange('specialInstructions', e.target.value)}
                    className="mt-1 focus:ring-teal-600 focus:border-teal-600 text-xs"
                    rows={2}
                  />
                </div>
                
                <div className="text-xs text-gray-600 bg-teal-50 p-2 rounded">
                  <p><strong>Note:</strong> Confirmation sent to info@emisamovers.com within 2 hours.</p>
                </div>
              </CardContent>
            </Card>

            {/* Submit Button - More Prominent */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 p-4 rounded-lg shadow-xl">
              <div className="text-center mb-2">
                <p className="text-white font-bold text-lg">Ready to Book Your Move?</p>
                <p className="text-white text-sm">Call us now or get this quote!</p>
              </div>
              <div className="flex flex-col gap-2">
                <a 
                  href="tel:7134879097"
                  className="flex items-center justify-center gap-2 bg-white hover:bg-gray-100 hover:shadow-2xl hover:scale-110 text-red-600 px-6 py-3 rounded-lg font-bold shadow-md transition-all duration-300 text-lg transform"
                >
                  <Phone className="h-5 w-5" />
                  Call (713) 487-9097
                </a>
                <Button
                  onClick={handleSubmitQuote}
                  disabled={isSubmitting}
                  size="lg"
                  className="w-full bg-teal-600 hover:bg-teal-700 hover:shadow-xl text-white font-bold text-lg py-6 transition-all duration-300"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      Get This Quote Now
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinalQuote;